import React, { useMemo, useRef, useState } from "react";
import styles from "./OrderPaymentDrawer.module.scss";

import {
  Col,
  Form,
  Modal,
  Row,
  Space,
  Typography,
  Button,
  Tooltip,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  applyVoucher,
  updateOrderPaymentStatus,
} from "services/booking.service";
import { CloseCircleOutlined } from "@ant-design/icons";
import { ReactComponent as CheckMarkIcon } from "assets/icons/checkMarkIco.svg";

import {
  FormCheckbox,
  FormHiddenSubmitButton,
  FormInput,
} from "components/FormItems/FlatFormItems";
import { setLoading } from "redux/actions/app.actions";
import { openNotificationWithIcon } from "utils/Notification";
import { ReactComponent as CloseIcon } from "assets/icons/closeIco.svg";
import { EXCLUSION_TYPE } from "utils/enums";
import Spinner from "components/Spinner/Spinner";
import { checkIfRewardIsApplied } from "utils/common";
import ReasonModal from "../../components/ReasonModal";

const { Title, Link } = Typography;

export default function AdjustmentsForm({
  form,
  order,
  customerWallets,
  isCustomerWalletsLoading,
  credit,
  total,
  handleWalletUpdate,
  setShowSelectWalletPopup,
  changeWalletPreferance,
  removeVoucher,
  handleUseWalletChecked
}) {
  const dispatch = useDispatch();
  const submitBtnRef = useRef();

  const useWallet = Form.useWatch("useWallet", form);
  const giftcard = total?.giftcard;
  const [showNoteModal, setShowNoteModal] = useState(false);

  // render variables start
  const isMobile = order?.operationTypeId === 1;
  const isOrderCancelled = order?.orderStatus === 2;
  const isOrderCompleted = order?.orderStatus === 3;

  const currentUser = useSelector((state) => state.user.currentUser);
  const hasDiscountManagementPermission = currentUser?.permissions?.find?.(
    (permission) => {
      return permission?.permissionName === "Discount Management";
    }
  );
  // render variables end

  const isVoucherAllowed = useMemo(() => {
    const hasRewards = checkIfRewardIsApplied(order);
    if (hasRewards && !hasDiscountManagementPermission) {
      return false;
    }
    // if (giftcard && !hasDiscountManagementPermission) {
    //   return false;
    // }
    return true;
  }, [order, hasDiscountManagementPermission, giftcard]);



  async function handleApplyVoucher(voucher, reason) {
    let response = await dispatch(
      applyVoucher(voucher, {
        orderId: order.orderId,
        reason,
      })
    );

    if (
      response?.status === "error" &&
      response?.type === "redemptionLimitExceeded"
    ) {
      Modal.confirm({
        title: "Redemption Limit Reached",
        okText: "Yes",
        cancelText: "No",
        content:
          "This voucher has reached its redemption limit. Do you want to still want to apply the voucher? ",
        onOk: async () => {
          const res = await dispatch(
            applyVoucher(
              voucher,
              {
                orderId: order.orderId,
                reason,
              },
              true
            )
          );
        },
        closeIcon: <CloseCircleOutlined />,
        maskClosable: true,
        closable: true,
      });
    }
  }

  return (
    <div style={{ padding: "20px 0px" }}>
      <p className={styles.messageText} style={{ margin: 0 }}>Adjustments</p>
      <Form
        name="voucherForm"
        layout="vertical"
        form={form}
        initialValues={{
          payment: order?.paymentType === 1 ? true : false,
          useWallet: order?.useWallet,
        }}
        requiredMark={false}
        onFinishFailed={({ values, errorFields, outOfDate }) => {
          form.scrollToField(errorFields[0].name, {
            scrollMode: "if-needed",
            block: "center",
            behavior: "smooth",
          });
        }}
        onFinish={async (values) => {
          if (!values.voucher) {
            openNotificationWithIcon(
              "error",
              "Error!",
              "No Voucher Code Provided"
            );
            return;
          }
          // Check if the order has Giftcard Balance
          // if (giftcard) {
          //   setShowNoteModal(true);
          //   return;
          // }

          // // Check if the order has rewards
          // const hasRewards = checkIfRewardIsApplied(order);
          // if (hasRewards) {
          //   setShowNoteModal(true);
          //   return;
          // }

          setShowNoteModal(true);
          // handleApplyVoucher(values.voucher);
        }}
      >
        <Row gutter={[16, 4]} className="couponsRow" hidden={true}>
          {!isMobile && (
            <Col span={16}>
              <FormCheckbox
                name="payment"
                label="Payment"
                formItemStyles={{ marginBottom: 0 }}
                disabled={isOrderCompleted || isOrderCancelled}
                onChange={async (e) => {
                  if (e.target.checked) {
                    dispatch(setLoading(true));
                    await dispatch(updateOrderPaymentStatus(order, 1));
                    dispatch(setLoading(false));
                  } else {
                    dispatch(setLoading(true));
                    await dispatch(updateOrderPaymentStatus(order, 2));
                    dispatch(setLoading(false));
                  }
                }}
              >
                Cash
              </FormCheckbox>
            </Col>
          )}
          <Col xs={24}>
            <Row>
              <Col>
                <FormCheckbox
                  hidden={
                    credit === 0 && !(isOrderCompleted && total?.giftcard > 0)
                  }
                  checked={order?.useWallet}
                  name="useWallet"
                  label="Use Wallet"
                  formItemStyles={{ marginBottom: 12 }}
                  disabled={
                    isOrderCompleted ||
                    isOrderCancelled ||
                    isCustomerWalletsLoading
                  }
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    if (checked === true) {
                      handleUseWalletChecked();
                    } else
                      dispatch(changeWalletPreferance(order?.orderId, checked));
                  }}
                >
                  Use Wallet
                </FormCheckbox>
              </Col>
              <Col>
                {isCustomerWalletsLoading && (
                  <Tooltip title="Loading Wallets">
                    <Spinner />
                  </Tooltip>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="couponsRow">
          <Col span={16}>
            <FormInput
              name="voucher"
              disabled={
                order?.voucherCode || isOrderCompleted || isOrderCancelled
                  ? true
                  : false
              }
              label="Apply Coupon"
              variant="underlined"
              placeholder="Eg: Code"
              formItemStyles={{ marginBottom: 0, marginTop: 8 }}
              initialValue={order?.voucherCode}
            />
            <FormHiddenSubmitButton submitRef={submitBtnRef}>
              submit
            </FormHiddenSubmitButton>
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            {order?.voucherCode ? (
              <Space className="voucherAppliedContainer">
                <div className="checkMarkOutlined">
                  <CheckMarkIcon />
                </div>
                <p className="tradeGothic availedText">Applied</p>
                <Button
                  disabled={isOrderCompleted || isOrderCancelled}
                  shape="circle"
                  className="voucherCloseBtn"
                  onClick={() => {
                    dispatch(removeVoucher(order?.orderId));
                  }}
                >
                  <CloseIcon />
                </Button>
              </Space>
            ) : (
              <Tooltip
                title={
                  !isVoucherAllowed
                    ? "Permission needed to override. Voucher can't be applied with Programs/Memberships/Giftcards."
                    : ""
                }
              >
                <Link
                  disabled={
                    isOrderCompleted || isOrderCancelled || !isVoucherAllowed
                  }
                  underline
                  style={{
                    paddingTop: 35,
                    fontSize: 16,
                    textDecoration: "underline",
                  }}
                  className="couponsRowApplyBtn"
                  onClick={() => {
                    if (submitBtnRef.current && !order?.voucherCode)
                      submitBtnRef.current.click();
                  }}
                >
                  Apply Now
                </Link>
              </Tooltip>
            )}
          </Col>
        </Row>
      </Form>
      <ReasonModal
        visible={showNoteModal}
        onSave={({note}) => {
          const voucher = form.getFieldValue("voucher");
          setShowNoteModal(false);
          handleApplyVoucher(voucher, note);
        }}
        onCancel={() => {
          setShowNoteModal(false);
        }}
        title="Add Note"
        text="Reason for applying a voucher."
        okButtonText="Add Voucher"
      />
    </div>
  );
}
